import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useAppSelector from '@sportson/core-web/hooks/useAppSelector';
import { stormSrc } from '@sportson/core-web/libs/image';
import decodeHTMLEntities from '@sportson/core-web/utils/decodeHTMLEntities';
import getCountrySpecificVariable from '@sportson/core-web/utils/getCountrySpecificVariable';
import { getMediaUrl } from '@sportson/core-web/utils/getMediaUrl';
import { getProductAvailability } from '@sportson/core-web/utils/getProductAvailability';
import { getHreflangs, getLanguages } from '@sportson/core-web/utils/multimarket';
import { getParametric } from '@sportson/core-web/utils/storm';
import { toFixedCurrency } from '@sportson/core-web/components/FormatPrice';
import LanguageLinks from '@sportson/core-web/components/Meta/LanguageLinks';
import ProductStructuredData from '@sportson/core-web/components/metadata/structuredData/ProductStructuredData';
import formatMetaDescription from '@sportson/core-web/components/metadata/utils/formatMetaDescription';
import { PARAMETRIC_MATERIAL_ID } from 'config/constants';

const getImagesFromFiles = (files) =>
    files.reduce((acc, cur) => {
        const { format } = cur;

        if (format.image) {
            return [...acc, getMediaUrl(format.image)];
        }

        return acc;
    }, []);

const getValidMetaDescription = (description) => {
    let strippedDescription = description;
    for (let i = 0; i < 3; i++) {
        if (strippedDescription.length > 155) {
            strippedDescription = strippedDescription.split('✓').slice(0, -1).join('✓');
        }
    }

    return strippedDescription;
};

const ProductMeta = ({ product, colorParametric }) => {
    const { t } = useTranslation();

    const site = useAppSelector(({ application }) => application.site) || {};
    const localeAlternates = useAppSelector(({ page }) => page.localeAlternates);

    if (!product) {
        return null;
    }

    const { protocol, origin } = site;
    const baseUrl = protocol + origin;
    const currency = site.currency?.code || null;

    const { eanCode, format, files, manufacturer, partNo, categories } = product;
    const imageSrc = (format || {}).image || (files[0] && files[0].format.image);
    const mpn = manufacturer?.partNo || null;
    const material = product.parametrics.find((x) => x.id === PARAMETRIC_MATERIAL_ID)?.value;
    const sku = partNo;
    const ean = eanCode;
    const brand = manufacturer?.name || manufacturer?.uniqueName;
    let price = product.price * product.vatRate;
    if (currency === 'EUR' || price % 1 !== 0) {
        price = toFixedCurrency(price, 2, '.');
    }
    const isUsedProduct = categories?.some((category) => category.id === 74043);

    const { includedProducts } = product;

    const images = getImagesFromFiles(product.files);
    imageSrc && images.unshift(stormSrc(imageSrc, { m: 'pad', auto: 'format' }, true));

    const seoProductTitle = getParametric('title_seo', product)?.value;
    const norceProductTitle = product.title || product.name;
    const titleFallback = t('seo.product_title_fallback', {
        product: [norceProductTitle, product.descriptionHeader].filter((x) => x).join(' - '),
    });

    const productMetaDescription = product.description;
    const metaDescriptionFallback = getValidMetaDescription(
        t('seo.product_description_fallback', {
            product: norceProductTitle,
            subTitle: product.descriptionHeader,
            price: product.price,
            uom: product.uom,
        }),
    );

    const title = seoProductTitle || titleFallback;
    const metaDescription = productMetaDescription || metaDescriptionFallback;

    const productUrl = `${baseUrl}${product.url}`;

    const keywords = product.keywords || null;

    const productAvailability = getProductAvailability(product, site.locale);

    const ogType = 'product';
    const ogTitle = title;
    const ogDescription = metaDescription || null;
    const ogImage = imageSrc;
    const ogUrl = productUrl || null;
    const ogLocale = site.locale || null;
    const ogProductPrice = typeof price === 'number' ? toFixedCurrency(price, 2, '.') : price;
    const ogProductCurrency = currency;
    const ogProductAvailability = productAvailability.inStock
        ? 'in stock'
        : productAvailability.preOrder || productAvailability.backOrder
          ? 'available for order'
          : 'out of stock';
    const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
    const facebookDomainVerification = getCountrySpecificVariable('REACT_APP_FACEBOOK_DOMAIN_VERIFICATION', ogLocale);

    let hreflangs;

    if (localeAlternates) {
        const languages = getLanguages(site.languages, ogLocale);
        hreflangs = getHreflangs(localeAlternates, languages);
    }

    return (
        <>
            <Helmet>
                {title && <title>{decodeHTMLEntities(title)}</title>}
                {productUrl && <link href={productUrl} rel="canonical" />}
                {metaDescription && (
                    <meta name="description" content={formatMetaDescription(decodeHTMLEntities(metaDescription))} />
                )}
                {keywords && <meta name="keywords" content={keywords} />}

                {ogType && <meta property="og:type" content={ogType} />}
                {ogTitle && <meta property="og:title" content={ogTitle} />}
                {ogDescription && <meta property="og:description" content={formatMetaDescription(ogDescription)} />}
                {ogImage && <meta property="og:image" content={ogImage} />}
                {ogUrl && <meta property="og:url" content={ogUrl} />}
                {ogLocale && <meta property="og:locale" content={ogLocale} />}
                {ogProductPrice && <meta property="og:price:amount" content={ogProductPrice} />}
                {ogProductCurrency && <meta property="og:price:currency" content={ogProductCurrency} />}
                {brand && <meta property="product:brand" content={brand} />}
                {ogProductAvailability && <meta property="product:availability" content={ogProductAvailability} />}
                <meta property="product:condition" content="new" />
                {ogLocale && <meta property="product:locale" content={ogLocale} />}
                {ogProductPrice && <meta property="product:price:amount" content={ogProductPrice} />}
                {ogProductCurrency && <meta property="product:price:currency" content={ogProductCurrency} />}
                {facebookAppId && <meta property="fb:app_id" content={facebookAppId} />}
                {facebookDomainVerification && (
                    <meta name="facebook-domain-verification" content={facebookDomainVerification} />
                )}
            </Helmet>
            <ProductStructuredData
                brand={brand}
                categories={product.categories}
                currency={currency}
                description={metaDescription}
                ean={ean}
                images={images}
                includedProducts={includedProducts}
                productAvailability={productAvailability}
                mpn={mpn}
                name={product.name}
                price={price}
                sku={sku}
                url={productUrl}
                variants={product.variants || []}
                averageScore={product.reviewsData?.averageScore}
                totalReviews={product.reviewsData?.totalReviews}
                isUsedProduct={isUsedProduct}
                statusId={product.statusId}
                color={colorParametric}
                material={material}
                product={product}
            />
            {hreflangs && <LanguageLinks links={hreflangs} />}
        </>
    );
};

ProductMeta.propTypes = {
    product: PropTypes.object.isRequired,
};

export default React.memo(ProductMeta, (prevProps, nextProps) => prevProps.product.partNo === nextProps.product.partNo);
